import React, { useEffect, useCallback } from 'react';
import './ConfirmModal.css';

/**
 * 커스텀 컨펌 모달 컴포넌트
 * @param {boolean} isOpen - 모달 표시 여부
 * @param {string} title - 모달 제목
 * @param {string} message - 확인 메시지
 * @param {string} confirmText - 확인 버튼 텍스트 (기본: '확인')
 * @param {string} cancelText - 취소 버튼 텍스트 (기본: '취소')
 * @param {string} type - 모달 타입: 'danger' | 'warning' | 'info' (기본: 'info')
 * @param {function} onConfirm - 확인 버튼 클릭 핸들러
 * @param {function} onCancel - 취소 버튼 클릭 핸들러
 */
const ConfirmModal = ({
    isOpen,
    title = '확인',
    message,
    confirmText = '확인',
    cancelText = '취소',
    type = 'info',
    onConfirm,
    onCancel
}) => {
    // ESC 키로 모달 닫기
    const handleKeyDown = useCallback((e) => {
        if (e.key === 'Escape') {
            onCancel?.();
        }
    }, [onCancel]);

    useEffect(() => {
        if (isOpen) {
            document.addEventListener('keydown', handleKeyDown);
            document.body.style.overflow = 'hidden';
        }
        return () => {
            document.removeEventListener('keydown', handleKeyDown);
            document.body.style.overflow = 'unset';
        };
    }, [isOpen, handleKeyDown]);

    if (!isOpen) return null;

    // 타입에 따른 아이콘
    const getIcon = () => {
        switch (type) {
            case 'danger':
                return (
                    <div className="confirm-modal-icon danger">
                        <svg viewBox="0 0 24 24" fill="currentColor">
                            <circle cx="12" cy="12" r="10" fill="none" stroke="currentColor" strokeWidth="2"/>
                            <rect x="11" y="6" width="2" height="8" rx="1"/>
                            <circle cx="12" cy="17" r="1.5"/>
                        </svg>
                    </div>
                );
            case 'warning':
                return (
                    <div className="confirm-modal-icon warning">
                        <svg viewBox="0 0 24 24" fill="currentColor">
                            <path d="M12 2L1 21h22L12 2z" fill="none" stroke="currentColor" strokeWidth="2" strokeLinejoin="round"/>
                            <rect x="11" y="9" width="2" height="5" rx="1"/>
                            <circle cx="12" cy="17" r="1.5"/>
                        </svg>
                    </div>
                );
            default:
                return (
                    <div className="confirm-modal-icon info">
                        <svg viewBox="0 0 24 24" fill="currentColor">
                            <circle cx="12" cy="12" r="10" fill="none" stroke="currentColor" strokeWidth="2"/>
                            <circle cx="12" cy="7" r="1.5"/>
                            <rect x="11" y="10" width="2" height="8" rx="1"/>
                        </svg>
                    </div>
                );
        }
    };

    return (
        <div className="confirm-modal-overlay" onClick={onCancel}>
            <div className="confirm-modal" onClick={(e) => e.stopPropagation()}>
                <div className="confirm-modal-content">
                    {getIcon()}
                    <h3 className="confirm-modal-title">{title}</h3>
                    <p className="confirm-modal-message">{message}</p>
                </div>
                <div className="confirm-modal-actions">
                    <button
                        className="confirm-modal-btn cancel"
                        onClick={onCancel}
                    >
                        {cancelText}
                    </button>
                    <button
                        className={`confirm-modal-btn confirm ${type}`}
                        onClick={onConfirm}
                    >
                        {confirmText}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default ConfirmModal;
